import {
  useIsExportableDataset,
  usePrepareScansExportData,
} from "@/modes/export-mode/scans-export/use-prepare-scans-export-data";
import { TreeData } from "@/utils/tree-generation/tree-data";
import { GUID } from "@faro-lotv/foundation";
import {
  createContext,
  PropsWithChildren,
  useContext,
  useMemo,
  type JSX,
} from "react";

type IpxScansData = {
  /** The clusters and scans of the selected point cloud, empty if it can only be sent as a whole */
  clusterTree: TreeData[];

  /** Clusters without scans that can be ordered */
  invalidClusterIds: Set<GUID>;
};

const NO_SCANS: IpxScansData = {
  clusterTree: [],
  invalidClusterIds: new Set(),
};

const IpxScansDataContext = createContext<IpxScansData>(NO_SCANS);

/**
 * The scans data can only be computed once the active element is in a dataset made of scans,
 * which is not yet the case when the wizard opens or for datasets that are sent as a whole
 *
 * @returns the provider of the scans of the selected point cloud
 */
export function IpxScansDataProvider({
  children,
}: PropsWithChildren): JSX.Element {
  if (!useIsExportableDataset()) {
    return (
      <IpxScansDataContext.Provider value={NO_SCANS}>
        {children}
      </IpxScansDataContext.Provider>
    );
  }
  return <ScansDataProvider>{children}</ScansDataProvider>;
}

function ScansDataProvider({ children }: PropsWithChildren): JSX.Element {
  const { clusterTree, invalidClusterIds } = usePrepareScansExportData();
  const value = useMemo(
    () => ({ clusterTree, invalidClusterIds }),
    [clusterTree, invalidClusterIds],
  );
  return (
    <IpxScansDataContext.Provider value={value}>
      {children}
    </IpxScansDataContext.Provider>
  );
}

/** @returns the scans of the selected point cloud, empty if it can only be sent as a whole */
export function useIpxScansData(): IpxScansData {
  return useContext(IpxScansDataContext);
}
